import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Field, Textarea } from '@/components/ui/field';
import { apiMessage } from '../helpers';

interface Plan {
  id: string;
  name: string;
  /** A decimal STRING from the API. It is displayed, never computed with. */
  priceMonthly: string;
  currency: string;
}

interface Props {
  /** The plan being asked for. `null` closes the dialog. */
  plan: Plan | null;
  currentPlanName: string;
  onClose: () => void;
  onSent: () => void;
}

/**
 * Asking the platform operator to move this organization.
 *
 * THIS IS NOT A CHECKOUT AND MUST NEVER BECOME ONE. The owner was offered card payment and
 * chose an approval instead, so there is no gateway anywhere in this product: no card fields,
 * no total, no "pay". What this dialog does is send a message and say plainly what happens
 * next — that a person will read it, and that nothing changes until they answer. Promising a
 * price here that the tenant then is not charged would be worse than saying nothing, so the
 * monthly figure is shown as the plan's price and labelled as such.
 *
 * The note is optional and matters more than it looks: "we are opening a second branch in
 * Jeddah" is the difference between an operator approving in a minute and one leaving the
 * request in a queue.
 */
export default function PlanRequestDialog({ plan, currentPlanName, onClose, onSent }: Props) {
  const { t, i18n } = useTranslation();
  const { code: fallbackCurrency } = useCurrency();
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (plan) setNote('');
  }, [plan]);

  const submit = async () => {
    if (!plan) return;
    setSending(true);
    try {
      await api.post('/plan-change-requests/mine', {
        planId: plan.id,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(t('settings.plan.requestSent'));
      onSent();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.plan.requestFailed')));
    } finally {
      setSending(false);
    }
  };

  const price = plan
    ? formatCurrency(plan.priceMonthly, plan.currency || fallbackCurrency, i18n.language)
    : '';

  return (
    <Dialog
      open={Boolean(plan)}
      onClose={onClose}
      title={t('settings.plan.requestTitle', { plan: plan?.name ?? '' })}
      description={t('settings.plan.requestHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={sending}>
            {sending ? t('settings.common.saving') : t('settings.plan.requestSubmit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <dl className="grid gap-3 rounded-lg bg-subtle p-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-ink-muted">{t('settings.plan.requestFrom')}</dt>
            <dd className="font-medium">{currentPlanName}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('settings.plan.requestTo')}</dt>
            <dd className="font-medium">
              {plan?.name} · {t('settings.plan.perMonth', { price })}
            </dd>
          </div>
        </dl>

        <Field label={t('settings.plan.requestNote')} hint={t('settings.plan.requestNoteHint')}>
          <Textarea
            rows={4}
            value={note}
            maxLength={1000}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('settings.plan.requestNotePlaceholder')}
          />
        </Field>

        <p className="text-xs text-ink-muted">{t('settings.plan.requestNoPayment')}</p>
      </div>
    </Dialog>
  );
}
